import type { PostFXSettingsPatch } from "../../../shared/api-contract";
import type { LookChange } from "./applyEngine";

/**
 * Looks — one-click scene styles for Basic mode.
 *
 * A look is a set of LOOK FIELD values (lookFields.ts), applied through the
 * Basic engine as ONE Undo step, so every Advanced slider it touches moves and
 * Compare / Reset treat it like any other change. The only thing outside the
 * look fields is whether the backdrop goes through tone mapping: a light look
 * turns that off so its white is genuinely #ffffff (not the ~#e2e2e2 a filmic
 * curve rolls it to), a dark look keeps it on for the filmic backdrop it was
 * tuned against. That flag is stated by every look, so switching is
 * deterministic.
 *
 * Every value sits inside its field's good-looking band.
 */
export interface Look {
  id: string;
  label: string;
  /** What it is for, in one line. */
  use: string;
  /** Tile fallback while the rendered thumbnail loads (or if it is missing). */
  swatch: string;
  values: LookChange[];
  fx: PostFXSettingsPatch;
}

const LIGHT_FX: PostFXSettingsPatch = { tonemapBackground: false, tonemapGround: false, vignette: { enabled: false }, grain: { enabled: false } };
const DARK_FX: PostFXSettingsPatch = { tonemapBackground: true, tonemapGround: true, vignette: { enabled: false }, grain: { enabled: false } };

export const LOOKS: readonly Look[] = [
  {
    id: "pure-white",
    label: "Pure white",
    use: "Amazon & Shopify ready",
    swatch: "linear-gradient(135deg,#ffffff,#ececea)",
    fx: LIGHT_FX,
    values: [
      { key: "light.background", value: "#ffffff" },
      { key: "light.exposure", value: 1.05 },
      { key: "light.contrast", value: 1.0 },
      { key: "light.saturation", value: 1.0 },
      { key: "bloom.enabled", value: false },
      { key: "shadow.enabled", value: true },
      { key: "shadow.darkness", value: 0.55 },
      { key: "shadow.softness", value: 0.6 },
      { key: "floor.reflection", value: false },
    ],
  },
  {
    id: "soft-studio",
    label: "Soft studio",
    use: "Calm, light grey set",
    swatch: "linear-gradient(135deg,#f6f5f2,#dcdad4)",
    fx: LIGHT_FX,
    values: [
      { key: "light.background", value: "#f1f0ec" },
      { key: "light.exposure", value: 1.0 },
      { key: "light.contrast", value: 0.95 },
      { key: "light.saturation", value: 1.0 },
      { key: "bloom.enabled", value: false },
      { key: "shadow.enabled", value: true },
      { key: "shadow.darkness", value: 0.4 },
      { key: "shadow.softness", value: 0.85 },
      { key: "floor.reflection", value: true },
      { key: "floor.reflectionStrength", value: 0.3 },
      { key: "floor.reflectionBlur", value: 0.55 },
    ],
  },
  {
    id: "black-velvet",
    label: "Black velvet",
    use: "Sparkle on black",
    swatch: "linear-gradient(135deg,#2a2a2e,#070708)",
    fx: DARK_FX,
    values: [
      { key: "light.background", value: "#0b0b0d" },
      { key: "light.exposure", value: 1.0 },
      { key: "light.contrast", value: 1.1 },
      { key: "light.saturation", value: 1.0 },
      { key: "bloom.enabled", value: true },
      { key: "bloom.intensity", value: 0.18 },
      { key: "bloom.threshold", value: 1.4 },
      { key: "shadow.enabled", value: false },
      { key: "floor.reflection", value: true },
      { key: "floor.reflectionStrength", value: 0.9 },
      { key: "floor.reflectionBlur", value: 0.2 },
    ],
  },
  {
    id: "warm-showroom",
    label: "Warm showroom",
    use: "Champagne, inviting",
    swatch: "linear-gradient(135deg,#f5ecdc,#dcc8a6)",
    fx: LIGHT_FX,
    values: [
      { key: "light.background", value: "#f2e8d8" },
      { key: "light.exposure", value: 1.04 },
      { key: "light.contrast", value: 1.05 },
      { key: "light.saturation", value: 1.08 },
      { key: "bloom.enabled", value: true },
      { key: "bloom.intensity", value: 0.1 },
      { key: "bloom.threshold", value: 1.8 },
      { key: "shadow.enabled", value: true },
      { key: "shadow.darkness", value: 0.5 },
      { key: "shadow.softness", value: 0.75 },
      { key: "floor.reflection", value: false },
    ],
  },
  {
    id: "editorial",
    label: "Editorial",
    use: "Moody, magazine style",
    swatch: "linear-gradient(135deg,#3a3129,#15110e)",
    fx: DARK_FX,
    values: [
      { key: "light.background", value: "#1a1512" },
      { key: "light.exposure", value: 0.96 },
      { key: "light.contrast", value: 1.18 },
      { key: "light.saturation", value: 0.92 },
      { key: "bloom.enabled", value: true },
      { key: "bloom.intensity", value: 0.3 },
      { key: "bloom.threshold", value: 1.2 },
      { key: "shadow.enabled", value: false },
      { key: "floor.reflection", value: true },
      { key: "floor.reflectionStrength", value: 0.55 },
      { key: "floor.reflectionBlur", value: 0.35 },
    ],
  },
];

/** A look the user saved from their own tuning (kept in this browser). */
export interface MyLook {
  id: string;
  label: string;
  values: LookChange[];
  background: string | null;
}

const MY_LOOKS_KEY = "studio.myLooks";

export function loadMyLooks(): MyLook[] {
  try {
    const raw = localStorage.getItem(MY_LOOKS_KEY);
    const parsed = raw ? (JSON.parse(raw) as MyLook[]) : [];
    return Array.isArray(parsed) ? parsed.filter((l) => l && typeof l.id === "string" && Array.isArray(l.values)) : [];
  } catch {
    return [];
  }
}

export function saveMyLooks(looks: MyLook[]) {
  try {
    localStorage.setItem(MY_LOOKS_KEY, JSON.stringify(looks.slice(0, 24)));
  } catch {
    /* private mode — the look still applies for this session */
  }
}
